/**
 * User Preferences Hooks
 *
 * React Query hooks for reading and writing user preferences.
 * Follows the query-key factory pattern used by other hooks in the app.
 */

import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userPreferencesService } from '@/shared/services/user-preferences-service';
import { PREF_KEYS } from '@/shared/constants';
import type {
  AiPreferences,
  AppPreferences,
  NotificationPreferences,
  Theme,
} from '@/shared/types/preferences';
import {
  DEFAULT_AI,
  DEFAULT_NOTIFICATIONS,
} from '@/shared/types/preferences';

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const userPreferencesKeys = {
  all: ['user-preferences'] as const,
  me: () => [...userPreferencesKeys.all, 'me'] as const,
};

// ---------------------------------------------------------------------------
// Core hooks
// ---------------------------------------------------------------------------

/** Fetch the current user's preferences. */
export function useUserPreferences() {
  return useQuery({
    queryKey: userPreferencesKeys.me(),
    queryFn: userPreferencesService.get,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/** Mutation to update (shallow-merge) preferences. */
export function useUpdateUserPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userPreferencesService.update,
    onSuccess: (data) => {
      // PUT returns the full object — use it directly
      queryClient.setQueryData(userPreferencesKeys.me(), data);
    },
  });
}

// ---------------------------------------------------------------------------
// Single-key helpers
// ---------------------------------------------------------------------------

/** Read a single top-level key from preferences. */
export function usePreference<K extends keyof AppPreferences>(key: K) {
  const { data } = useUserPreferences();
  return useMemo(
    () => (data?.preferences as AppPreferences | undefined)?.[key],
    [data, key],
  );
}

/** Returns a stable updater function for a single top-level key. */
export function useUpdatePreference<K extends keyof AppPreferences>(key: K) {
  const { data } = useUserPreferences();
  const mutation = useUpdateUserPreferences();

  return useCallback(
    (value: AppPreferences[K]) => {
      const prefs = (data?.preferences ?? {}) as AppPreferences;

      // For nested objects, merge with the existing stored value
      let merged: AppPreferences[K] = value;
      if (key === PREF_KEYS.NOTIFICATIONS && typeof value === 'object') {
        merged = { ...prefs.notifications, ...value } as AppPreferences[K];
      } else if (key === PREF_KEYS.AI && typeof value === 'object') {
        merged = { ...prefs.ai, ...value } as AppPreferences[K];
      }

      mutation.mutate({ preferences: { [key]: merged } });
    },
    [data, key, mutation],
  );
}

// ---------------------------------------------------------------------------
// Typed convenience hooks
// ---------------------------------------------------------------------------

/** Current theme preference, defaulting to 'system'. */
export function useThemePreference(): Theme {
  const theme = usePreference(PREF_KEYS.THEME);
  return theme ?? 'system';
}

/** List of dismissed budget suggestion category IDs. */
export function useDismissedBudgetSuggestions(): string[] {
  const dismissed = usePreference(PREF_KEYS.DISMISSED_BUDGET_SUGGESTIONS);
  return dismissed ?? [];
}

/** Notification preferences with defaults applied. */
export function useNotificationPreferences(): NotificationPreferences {
  const stored = usePreference(PREF_KEYS.NOTIFICATIONS);
  return useMemo(
    () => ({ ...DEFAULT_NOTIFICATIONS, ...stored }),
    [stored],
  );
}

/** AI preferences with defaults applied. */
export function useAiPreferences(): AiPreferences {
  const stored = usePreference(PREF_KEYS.AI);
  return useMemo(() => ({ ...DEFAULT_AI, ...stored }), [stored]);
}
